'use client';
import { ReactNode, createRef, useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

type Props = {
    title?: ReactNode;
    className?: string;
    items: { href: string; title: string; extra?: ReactNode; active?: boolean }[];
};

export default function AsideMenu({ items, title, className }: Props) {
    const [displayLeftShadow, setDisplayLeftShadow] = useState(false);
    const [displayRightShadow, setDisplayRightShadow] = useState(false);
    const scrollDivRef = createRef<HTMLDivElement>();
    const pathname = usePathname();

    useEffect(() => {
        const div = scrollDivRef.current;
        if (!div) return;

        const computeShadows = () => {
            setDisplayLeftShadow(div.scrollLeft > 0);
            setDisplayRightShadow(Math.ceil(div.scrollLeft + div.clientWidth) < div.scrollWidth);
        };

        window.addEventListener('resize', computeShadows);
        div.addEventListener('scroll', computeShadows);
        computeShadows();

        return () => {
            window.removeEventListener('resize', computeShadows);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const activeItem = items.find((item) => item.active);
        const a = Array.from(scrollDivRef?.current?.querySelectorAll('a') || []).find(
            (a) => a.href.indexOf(activeItem ? activeItem.href : pathname || '') > -1,
        );
        a?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'center' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, items]);

    const activeItemIndex = items.findIndex((item) => item.active);
    const activeIndex =
        activeItemIndex > -1 ? activeItemIndex : items.findLastIndex((i) => pathname?.indexOf(i.href) === 0);

    return (
        <div className={`top-nav-sm-height z-10 sticky${className ? ` ${className}` : ''}`}>
            <div className="lg:top-nav-md-height sticky lg:py-10">
                {title && <div className="bg-primary font-display hidden p-4 text-xl text-white lg:block">{title}</div>}
                <div className="relative">
                    <div
                        ref={scrollDivRef}
                        className="scrollbar-hide container relative mx-auto snap-x snap-mandatory overflow-scroll bg-white p-4 lg:px-0">
                        <ul className="flex gap-8 lg:block">
                            {items.map((item, index) => {
                                const active = index === activeIndex;

                                return (
                                    <li
                                        key={item.href}
                                        className={`relative shrink-0 snap-center ${
                                            active
                                                ? 'before:border-primary before:absolute before:bottom-0 before:left-0 before:right-0 before:border-b-4 lg:before:right-auto lg:before:top-0 lg:before:border-b-0 lg:before:border-l-4'
                                                : ''
                                        }`}>
                                        <Link
                                            href={item.href}
                                            className="group flex items-baseline gap-4 py-2 lg:p-4 lg:py-3">
                                            <div className="font-display flex-1 text-lg group-hover:underline">
                                                {item.title}
                                            </div>
                                            {item.extra}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                    {displayLeftShadow && (
                        <div
                            className="pointer-events-none absolute bottom-0 left-0 top-0 w-1/5 lg:hidden"
                            style={{
                                background:
                                    'transparent linear-gradient(90deg, #FFFFFF 0%, #FFFFFF00 100%) 0% 0% no-repeat padding-box',
                            }}
                        />
                    )}
                    {displayRightShadow && (
                        <div
                            className="pointer-events-none absolute bottom-0 right-0 top-0 w-1/5 lg:hidden"
                            style={{
                                background:
                                    'transparent linear-gradient(90deg, #FFFFFF00 0%, #FFFFFF 100%) 0% 0% no-repeat padding-box',
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
